import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '../../models/user/permissions';
import useDateFormat from '../../hooks/useDateFormat';
import { locationOrigin } from '../../utils/utils';

import { ButtonDropdown, Dropdown, Icon, Card, Chip, CopyToClipboardLink, Button, StopPropagation } from '@weezevent/nacre';
import { PlaceholderImage } from '../../images';
import { BoItems } from 'components';

import css from './events.module.css';

const EventCard = React.memo(({ event }) => {
    const { t } = useTranslation();
    const user = usePermissions();
    const window_origin = locationOrigin();

    return (
        <Card key={event.id} className={css['event-card']}>
            <div className={css['event-card-top']}>
                <div className={css['event-card-infos']}>
                    <div
                        className={css['event-card-banner-container']}
                        style={{
                            backgroundImage: `url(${event.full_picture || PlaceholderImage})`
                        }}
                    />
                    <div className={css['event-row-infos']}>
                        <div className={css['event-infos-container']}>
                            <div className={css['event-orga-items-container']}>
                                <div className={css['event-ids-container']}>
                                    <Icon name="institution" />
                                    <div className={css['event-id']}>O {event.organizationId}</div>
                                </div>
                                <div className={css['event-ids-container']}>
                                    <Icon name="calendar-star" />
                                    <div className={css['event-id']}>E {event.id}</div>
                                </div>
                            </div>
                            <div className={css['event-name']}>{event.getSafeName(t)}</div>
                        </div>
                        <div className={css['event-infos-container']}>
                            <div className={css['event-item-container']}>
                                <Icon name="calendar" />
                                <div className={css['event-id']}>
                                    {event.start_date ? useDateFormat({ date: event.start_date, event }).properDateAndTime : t('common.date.undefined')}
                                </div>
                                <span>({event.start_date && useDateFormat({ date: event.start_date, event }).timeZone})</span>
                                <EventChipStatus event={event} />
                            </div>
                            {event.gemino_url && (
                                <div className={css['event-item-container']}>
                                    <Icon name="globe" />
                                    {t('sauron.events.minisite')}:<div className={css['minisite-url']}>{event.gemino_url}</div>
                                    <Icon name="external-link" onClick={() => window.open(`${event.gemino_url}`)} />
                                    <CopyToClipboardLink tooltipMini delay={1000} value={event.gemino_url} copyOnIconClick />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <StopPropagation>
                    <ButtonDropdown
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('sauron.events.actions')} onlyIconMobile={false} />}
                        items={[
                            <div key="items" onClick={e => e.preventDefault()}>
                                <Dropdown.Item key={0} onClick={() => window.open(`${window_origin}/ticket/O${event.organizationId}/E${event.id}/settings/event`)}>
                                    {t('sauron.events.event_redirection')}
                                    <Icon className={css['event-dropdown-item']} name="external-link" />
                                </Dropdown.Item>
                                {user.isOrganizationOwner(event.organizationId) && (
                                    <Dropdown.Item key={1} onClick={() => window.open(`${window_origin}/ticket/O${event.organizationId}/organization/profile`)}>
                                        {t('sauron.events.organization_settings')}
                                        <Icon className={css['event-dropdown-item']} name="external-link" />
                                    </Dropdown.Item>
                                )}
                            </div>
                        ]}
                    />
                </StopPropagation>
            </div>
            <BoItems organizationId={event.organizationId} eventId={event.id} />
        </Card>
    );
});

export default EventCard;

function EventChipStatus({ event }) {
    const { t } = useTranslation();
    const status = event.getStatusInfo();

    return (
        <div className={css['event-chip']}>
            <Chip primary={false} inverted={true} text={t(`sauron.events.time-status.${status.text}`)} status={status.css} />
        </div>
    );
}
